import type { Layer } from 'ol/layer'
import type ImageryLayer from '@arcgis/core/layers/ImageryLayer'

export type Region = 'nl' | 'be' | 'de' | 'fr'

export type MapPlatform = 'ol' | 'arcgis'

export interface LayerDefinition {
  name: string
  factory: () => Promise<Layer | ImageryLayer | null>
  immediateLoad: boolean  // true for WMS (tiles load on-demand), false for vector
  regions?: Region[]      // Geographic coverage metadata
  platform?: MapPlatform  // 'ol' (default) or 'arcgis' - which map engine handles this layer
}

// Layer registry - NL only version
// WMS/tile layers keep immediateLoad metadata for classification only.
// The app creates all non-base layers on demand to improve startup time.

export const layerRegistry: Record<string, LayerDefinition> = {
  // ============================================
  // SPECIAL ANALYSIS LAYERS
  // ============================================

  'Kansenkaart': {
    name: 'Kansenkaart',
    factory: async () => {
      const { createKansenkaartLayerOL } = await import('./kansenkaartOL')
      return createKansenkaartLayerOL()
    },
    immediateLoad: false  // Lazy load - lots of data to combine
  },

  // ============================================
  // BASE LAYERS - Created in MapContainer, but need tier info here
  // ============================================
  'TMK 1850': {
    name: 'TMK 1850',
    factory: async () => null,  // Created in MapContainer
    immediateLoad: true
  },
  'Bonnebladen 1900': {
    name: 'Bonnebladen 1900',
    factory: async () => null,  // Created in MapContainer
    immediateLoad: true
  },

  // ============================================
  // IMMEDIATE LOAD - WMS/Tile layers
  // These are loaded at startup but tiles only fetch when visible
  // ============================================

  // PDOK WMS layers
  'AHN 0.5m': {
    name: 'AHN 0.5m',
    factory: async () => {
      const { createAHNLayerOL } = await import('./pdokWMSLayers')
      return createAHNLayerOL()
    },
    immediateLoad: true
  },
  'Geomorfologie': {
    name: 'Geomorfologie',
    factory: async () => {
      const { createGeomorfologieLayerOL } = await import('./pdokWMSLayers')
      return createGeomorfologieLayerOL()
    },
    immediateLoad: true
  },
  'Bodemkaart': {
    name: 'Bodemkaart',
    factory: async () => {
      const { createBodemkaartLayerOL } = await import('./pdokWMSLayers')
      return createBodemkaartLayerOL()
    },
    immediateLoad: true
  },
  'IKAW': {
    name: 'IKAW',
    factory: async () => {
      const { createIKAWLayerOL } = await import('./pdokWMSLayers')
      return createIKAWLayerOL()
    },
    immediateLoad: true
  },
  'FAMKE Steentijd': {
    name: 'FAMKE Steentijd',
    factory: async () => {
      const { createFAMKESteentijdLayerOL } = await import('./pdokWMSLayers')
      return createFAMKESteentijdLayerOL()
    },
    immediateLoad: true
  },
  'FAMKE IJzertijd': {
    name: 'FAMKE IJzertijd',
    factory: async () => {
      const { createFAMKEIJzertijdLayerOL } = await import('./pdokWMSLayers')
      return createFAMKEIJzertijdLayerOL()
    },
    immediateLoad: true
  },
  'Essen': {
    name: 'Essen',
    factory: async () => {
      const { createEssenLayerOL } = await import('./pdokWMSLayers')
      return createEssenLayerOL()
    },
    immediateLoad: true
  },

  // RCE WMS layers - Erfgoed & Monumenten
  'Rijksmonumenten': {
    name: 'Rijksmonumenten',
    factory: async () => {
      const { createRijksmonumentenLayerOL } = await import('./pdokWMSLayers')
      return createRijksmonumentenLayerOL()
    },
    immediateLoad: true
  },
  'Archeo Onderzoeken': {
    name: 'Archeo Onderzoeken',
    factory: async () => {
      const { createArcheoOnderzoekenLayerOL } = await import('./pdokWMSLayers')
      return createArcheoOnderzoekenLayerOL()
    },
    immediateLoad: true
  },
  'Werelderfgoed': {
    name: 'Werelderfgoed',
    factory: async () => {
      const { createWerelderfgoedLayerOL } = await import('./pdokWMSLayers')
      return createWerelderfgoedLayerOL()
    },
    immediateLoad: true
  },

  // Friesland WMS layers
  'Terpen': {
    name: 'Terpen',
    factory: async () => {
      const { createTerpenLayerOL } = await import('./terpenOL')
      return createTerpenLayerOL()
    },
    immediateLoad: true
  },

  // RCE Verdedigingswerken (Linies en Stellingen)
  'Verdedigingslinies': {
    name: 'Verdedigingslinies',
    factory: async () => {
      const { createLiniesLayerOL } = await import('./verdedigingswerkenOL')
      return createLiniesLayerOL()
    },
    immediateLoad: true
  },
  'Inundatiegebieden': {
    name: 'Inundatiegebieden',
    factory: async () => {
      const { createInundatiesLayerOL } = await import('./verdedigingswerkenOL')
      return createInundatiesLayerOL()
    },
    immediateLoad: true
  },
  'Militaire Objecten': {
    name: 'Militaire Objecten',
    factory: async () => {
      const { createMilitaireObjectenLayerOL } = await import('./verdedigingswerkenOL')
      return createMilitaireObjectenLayerOL()
    },
    immediateLoad: true
  },

  // RCE Paleogeografische kaarten
  'Paleokaart 800 n.Chr.': {
    name: 'Paleokaart 800 n.Chr.',
    factory: async () => {
      const { createPaleo800ncLayerOL } = await import('./paleokaartOL')
      return createPaleo800ncLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 100 n.Chr.': {
    name: 'Paleokaart 100 n.Chr.',
    factory: async () => {
      const { createPaleo100ncLayerOL } = await import('./paleokaartOL')
      return createPaleo100ncLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 500 v.Chr.': {
    name: 'Paleokaart 500 v.Chr.',
    factory: async () => {
      const { createPaleo500vcLayerOL } = await import('./paleokaartOL')
      return createPaleo500vcLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 1500 v.Chr.': {
    name: 'Paleokaart 1500 v.Chr.',
    factory: async () => {
      const { createPaleo1500vcLayerOL } = await import('./paleokaartOL')
      return createPaleo1500vcLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 2750 v.Chr.': {
    name: 'Paleokaart 2750 v.Chr.',
    factory: async () => {
      const { createPaleo2750vcLayerOL } = await import('./paleokaartOL')
      return createPaleo2750vcLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 5500 v.Chr.': {
    name: 'Paleokaart 5500 v.Chr.',
    factory: async () => {
      const { createPaleo5500vcLayerOL } = await import('./paleokaartOL')
      return createPaleo5500vcLayerOL()
    },
    immediateLoad: true
  },
  'Paleokaart 9000 v.Chr.': {
    name: 'Paleokaart 9000 v.Chr.',
    factory: async () => {
      const { createPaleo9000vcLayerOL } = await import('./paleokaartOL')
      return createPaleo9000vcLayerOL()
    },
    immediateLoad: true
  },

  // RCE Religieus Erfgoed
  'Religieus Erfgoed': {
    name: 'Religieus Erfgoed',
    factory: async () => {
      const { createReligieusErfgoedLayerOL } = await import('./religieusErfgoedOL')
      return createReligieusErfgoedLayerOL()
    },
    immediateLoad: true
  },

  // Hillshade layers - NL only (OpenLayers ImageArcGISRest - geen API key nodig)
  'AHN4 Hoogtekaart Kleur': {
    name: 'AHN4 Hoogtekaart Kleur',
    factory: async () => {
      const { createAHN4ColorElevationLayerOL } = await import('./hillshadeLayers')
      return createAHN4ColorElevationLayerOL()
    },
    immediateLoad: true
  },
  'AHN4 Hillshade NL': {
    name: 'AHN4 Hillshade NL',
    factory: async () => {
      const { createAHN4HillshadeLayerOL } = await import('./hillshadeLayers')
      return createAHN4HillshadeLayerOL()
    },
    immediateLoad: true
  },
  'AHN4 Multi-Hillshade NL': {
    name: 'AHN4 Multi-Hillshade NL',
    factory: async () => {
      const { createAHN4MultiHillshadeLayerOL } = await import('./hillshadeLayers')
      return createAHN4MultiHillshadeLayerOL()
    },
    immediateLoad: true
  },
  'AHN4 Hillshade Kleur': {
    name: 'AHN4 Hillshade Kleur',
    factory: async () => {
      const { createAHN4ShadedReliefLayerOL } = await import('./hillshadeLayers')
      return createAHN4ShadedReliefLayerOL()
    },
    immediateLoad: true
  },

  // World Hillshade VERWIJDERD - Esri commercieel, geen toestemming

  // Percelen - Kadaster & Landbouw
  'Gewaspercelen': {
    name: 'Gewaspercelen',
    factory: async () => {
      const { createGewaspercelenLayerOL } = await import('./pdokWMSLayers')
      return createGewaspercelenLayerOL()
    },
    immediateLoad: true
  },
  'Kadastrale Grenzen': {
    name: 'Kadastrale Grenzen',
    factory: async () => {
      const { createKadastraleKaartLayerOL } = await import('./pdokWMSLayers')
      return createKadastraleKaartLayerOL()
    },
    immediateLoad: true
  },

  // Provinciale Cultuurhistorische Waardenkaarten

  // Zuid-Holland
  'Scheepswrakken': {
    name: 'Scheepswrakken',
    factory: async () => {
      const { createScheepswrakkenLayerOL } = await import('./pdokWMSLayers')
      return createScheepswrakkenLayerOL()
    },
    immediateLoad: true
  },
  'Woonheuvels ZH': {
    name: 'Woonheuvels ZH',
    factory: async () => {
      const { createWoonheuvelsLayerOL } = await import('./pdokWMSLayers')
      return createWoonheuvelsLayerOL()
    },
    immediateLoad: true
  },
  'Romeinse Forten': {
    name: 'Romeinse Forten',
    factory: async () => {
      const { createRomeinseFortenLayerOL } = await import('./romeinseFortenOL')
      return createRomeinseFortenLayerOL()
    },
    immediateLoad: false  // Vector layer, lazy load
  },
  'Windmolens': {
    name: 'Windmolens',
    factory: async () => {
      const { createWindmolensLayerOL } = await import('./pdokWMSLayers')
      return createWindmolensLayerOL()
    },
    immediateLoad: true
  },
  'Erfgoedlijnen': {
    name: 'Erfgoedlijnen',
    factory: async () => {
      const { createErfgoedlijnenLayerOL } = await import('./pdokWMSLayers')
      return createErfgoedlijnenLayerOL()
    },
    immediateLoad: true
  },
  'Oude Kernen': {
    name: 'Oude Kernen',
    factory: async () => {
      const { createOudeKernenLayerOL } = await import('./pdokWMSLayers')
      return createOudeKernenLayerOL()
    },
    immediateLoad: true
  },

  // Gelderland - Relictenkaart (Punten nu met eigen iconen via WFS)
  'Relictenkaart Punten': {
    name: 'Relictenkaart Punten',
    factory: async () => {
      const { createRelictenPuntenLayerOL } = await import('./relictenGelderlandOL')
      return createRelictenPuntenLayerOL()
    },
    immediateLoad: false  // Lazy load - data fetched when visible
  },
  'Relictenkaart Lijnen': {
    name: 'Relictenkaart Lijnen',
    factory: async () => {
      const { createRelictenkaartLijnenLayerOL } = await import('./pdokWMSLayers')
      return createRelictenkaartLijnenLayerOL()
    },
    immediateLoad: true
  },
  'Relictenkaart Vlakken': {
    name: 'Relictenkaart Vlakken',
    factory: async () => {
      const { createRelictenkaartVlakkenLayerOL } = await import('./pdokWMSLayers')
      return createRelictenkaartVlakkenLayerOL()
    },
    immediateLoad: true
  },

  // Zeeland
  'Verdronken Dorpen': {
    name: 'Verdronken Dorpen',
    factory: async () => {
      const { createVerdronkenDorpenLayerOL } = await import('./pdokWMSLayers')
      return createVerdronkenDorpenLayerOL()
    },
    immediateLoad: true
  },

  // ============================================
  // LAZY LOAD - Vector layers
  // These are only loaded when first toggled on
  // ============================================

  // Archaeological vector layers - AMK with period filters
  'AMK Monumenten': {
    name: 'AMK Monumenten',
    factory: async () => {
      const { createAMKLayerOL } = await import('./amkOL')
      return createAMKLayerOL()
    },
    immediateLoad: false
  },
  'AMK Romeins': {
    name: 'AMK Romeins',
    factory: async () => {
      const { createAMKRomeinsLayerOL } = await import('./amkOL')
      return createAMKRomeinsLayerOL()
    },
    immediateLoad: false
  },
  'AMK Steentijd': {
    name: 'AMK Steentijd',
    factory: async () => {
      const { createAMKSteentijdLayerOL } = await import('./amkOL')
      return createAMKSteentijdLayerOL()
    },
    immediateLoad: false
  },
  'AMK Vroege ME': {
    name: 'AMK Vroege ME',
    factory: async () => {
      const { createAMKVroegeMELayerOL } = await import('./amkOL')
      return createAMKVroegeMELayerOL()
    },
    immediateLoad: false
  },
  'AMK Late ME': {
    name: 'AMK Late ME',
    factory: async () => {
      const { createAMKLateMELayerOL } = await import('./amkOL')
      return createAMKLateMELayerOL()
    },
    immediateLoad: false
  },
  'AMK Overig': {
    name: 'AMK Overig',
    factory: async () => {
      const { createAMKOverigLayerOL } = await import('./amkOL')
      return createAMKOverigLayerOL()
    },
    immediateLoad: false
  },
  'Romeinse wegen (regio)': {
    name: 'Romeinse wegen (regio)',
    factory: async () => {
      const { createRomeinseWegenLayerOL } = await import('./romeinsOL')
      return createRomeinseWegenLayerOL()
    },
    immediateLoad: false
  },
  'Romeinse wegen (Wereld)': {
    name: 'Romeinse wegen (Wereld)',
    factory: async () => {
      const { createRomeinseWegenWereldLayerOL } = await import('./romeinsOL')
      return createRomeinseWegenWereldLayerOL()
    },
    immediateLoad: false,
    regions: ['nl', 'be', 'de', 'fr']  // Internationale laag
  },
  'Kastelen': {
    name: 'Kastelen',
    factory: async () => {
      const { createKastelenLayerOL } = await import('./kastelenOL')
      return createKastelenLayerOL()
    },
    immediateLoad: false
  },
  'Ruïnes': {
    name: 'Ruïnes',
    factory: async () => {
      const { createRuinesLayerOL } = await import('./ruinesOL')
      return createRuinesLayerOL()
    },
    immediateLoad: false
  },
  'Veengebieden': {
    name: 'Veengebieden',
    factory: async () => {
      const { createVeengebiedenLayerOL } = await import('./veengebiedenOL')
      return createVeengebiedenLayerOL()
    },
    immediateLoad: false
  },

  // Steentijd (Stone Age) layers
  'Hunebedden': {
    name: 'Hunebedden',
    factory: async () => {
      const { createHunebeddenLayerOL } = await import('./hunebeddenOL')
      return createHunebeddenLayerOL()
    },
    immediateLoad: false
  },
  // UIKAV layers
  'UIKAV Punten': {
    name: 'UIKAV Punten',
    factory: async () => {
      const { createUIKAVArcheoPuntenLayerOL } = await import('./uikavArcheoPuntenOL')
      return createUIKAVArcheoPuntenLayerOL()
    },
    immediateLoad: false
  },
  'UIKAV Vlakken': {
    name: 'UIKAV Vlakken',
    factory: async () => {
      const { createUIKAVVlakkenLayerOL } = await import('./uikavVlakkenOL')
      return createUIKAVVlakkenLayerOL()
    },
    immediateLoad: false
  },
  'UIKAV Buffer': {
    name: 'UIKAV Buffer',
    factory: async () => {
      const { createUIKAVBufferlaagLayerOL } = await import('./uikavBufferlaagOL')
      return createUIKAVBufferlaagLayerOL()
    },
    immediateLoad: false
  },
  'UIKAV Expert': {
    name: 'UIKAV Expert',
    factory: async () => {
      const { createUIKAVExpertLayerOL } = await import('./uikavExpertOL')
      return createUIKAVExpertLayerOL()
    },
    immediateLoad: false
  },
  'UIKAV Indeling': {
    name: 'UIKAV Indeling',
    factory: async () => {
      const { createUIKAVUiterwaardenLayerOL } = await import('./uikavUiterwaardenOL')
      return createUIKAVUiterwaardenLayerOL()
    },
    immediateLoad: false
  },

  // Recreation layers
  'Parken': {
    name: 'Parken',
    factory: async () => {
      const { createParkenLayerOL } = await import('./parkenOL')
      return createParkenLayerOL()
    },
    immediateLoad: false
  },
  'Speeltuinen': {
    name: 'Speeltuinen',
    factory: async () => {
      const { createSpeeltuinenLayerOL } = await import('./speeltuinenOL')
      return createSpeeltuinenLayerOL()
    },
    immediateLoad: false
  },
  'Strandjes': {
    name: 'Strandjes',
    factory: async () => {
      const { createStrandjesLayerOL } = await import('./strandjesOL')
      return createStrandjesLayerOL()
    },
    immediateLoad: false
  },

  // Fossil layers - PBDB data
  'Fossielen Nederland': {
    name: 'Fossielen Nederland',
    factory: async () => {
      const { createFossielenLayerOL } = await import('./fossielenOL')
      return createFossielenLayerOL()
    },
    immediateLoad: false
  },
  'Fossielen België': {
    name: 'Fossielen België',
    factory: async () => {
      const { createFossielenBelgieLayerOL } = await import('./fossielenBelgieOL')
      return createFossielenBelgieLayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },
  'Fossielen Duitsland': {
    name: 'Fossielen Duitsland',
    factory: async () => {
      const { createFossielenDuitslandLayerOL } = await import('./fossielenDuitslandOL')
      return createFossielenDuitslandLayerOL()
    },
    immediateLoad: false,
    regions: ['de']
  },
  'Fossielen Frankrijk': {
    name: 'Fossielen Frankrijk',
    factory: async () => {
      const { createFossielenFrankrijkLayerOL } = await import('./fossielenFrankrijkOL')
      return createFossielenFrankrijkLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Fossiel Hotspots': {
    name: 'Fossiel Hotspots',
    factory: async () => {
      const { createFossielHotspotsLayerOL } = await import('./fossielHotspotsOL')
      return createFossielHotspotsLayerOL()
    },
    immediateLoad: false,
    regions: ['nl', 'be', 'de', 'fr']
  },
  'Mineralen Hotspots': {
    name: 'Mineralen Hotspots',
    factory: async () => {
      const { createMineralenHotspotsLayerOL } = await import('./mineralenHotspotsOL')
      return createMineralenHotspotsLayerOL()
    },
    immediateLoad: false,
    regions: ['nl', 'be', 'de', 'fr']  // Nu ook NL locaties
  },
  'Goudrivieren': {
    name: 'Goudrivieren',
    factory: async () => {
      const { createGoudrivierenLayerOL } = await import('./goudrivierenOL')
      return createGoudrivierenLayerOL()
    },
    immediateLoad: false,
    regions: ['nl', 'be', 'de', 'fr']  // Internationale laag
  },

  // WWII Bunkers - from OpenStreetMap
  'WWII Bunkers': {
    name: 'WWII Bunkers',
    factory: async () => {
      const { createBunkersLayerOL } = await import('./bunkersOL')
      return createBunkersLayerOL()
    },
    immediateLoad: false
  },

  // Grafheuvels (burial mounds) - from OpenStreetMap
  'Grafheuvels': {
    name: 'Grafheuvels',
    factory: async () => {
      const { createGrafheuvelsLayerOL } = await import('./grafheuvelsOL')
      return createGrafheuvelsLayerOL()
    },
    immediateLoad: false
  },

  // Military layers - WWII related
  'Slagvelden': {
    name: 'Slagvelden',
    factory: async () => {
      const { createSlagveldenLayerOL } = await import('./slagveldenOL')
      return createSlagveldenLayerOL()
    },
    immediateLoad: false
  },
  'Militaire Vliegvelden': {
    name: 'Militaire Vliegvelden',
    factory: async () => {
      const { createVliegveldenLayerOL } = await import('./vliegveldenOL')
      return createVliegveldenLayerOL()
    },
    immediateLoad: false
  },

  // ============================================
  // INTERNATIONAL LAYERS - BELGIUM
  // ============================================

  'Monumenten BE': {
    name: 'Monumenten BE',
    factory: async () => {
      const { createBeschermdeMonumentenBELayerOL } = await import('./belgieErfgoedLayers')
      return createBeschermdeMonumentenBELayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },
  'Archeo Zones BE': {
    name: 'Archeo Zones BE',
    factory: async () => {
      const { createArcheoZonesBELayerOL } = await import('./belgieErfgoedLayers')
      return createArcheoZonesBELayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },
  'Arch Sites BE': {
    name: 'Arch Sites BE',
    factory: async () => {
      const { createBeschArchSitesBELayerOL } = await import('./belgieErfgoedLayers')
      return createBeschArchSitesBELayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },
  'Erfgoed Landschap BE': {
    name: 'Erfgoed Landschap BE',
    factory: async () => {
      const { createErfgoedLandschappenBELayerOL } = await import('./belgieErfgoedLayers')
      return createErfgoedLandschappenBELayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },
  'CAI Elementen': {
    name: 'CAI Elementen',
    factory: async () => {
      const { createCAIElementenBELayerOL } = await import('./belgieErfgoedLayers')
      return createCAIElementenBELayerOL()
    },
    immediateLoad: false,
    regions: ['be']
  },

  // ============================================
  // INTERNATIONAL LAYERS - FRANCE
  // ============================================

  'Hist. Gebouwen FR': {
    name: 'Hist. Gebouwen FR',
    factory: async () => {
      const { createFrankrijkMonumentenLayerOL } = await import('./frankrijkMonumentenOL')
      return createFrankrijkMonumentenLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'INRAP Sites FR': {
    name: 'INRAP Sites FR',
    factory: async () => {
      const { createInrapSitesFRLayerOL } = await import('./inrapSitesFROL')
      return createInrapSitesFRLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Archeo Sites Bretagne': {
    name: 'Archeo Sites Bretagne',
    factory: async () => {
      const { createArcheoBretagneLayerOL } = await import('./archeoBretagneOL')
      return createArcheoBretagneLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Operaties Bretagne': {
    name: 'Operaties Bretagne',
    factory: async () => {
      const { createBretagneOperationsLayerOL } = await import('./bretagneOperationsOL')
      return createBretagneOperationsLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Archeo Parijs': {
    name: 'Archeo Parijs',
    factory: async () => {
      const { createParisArcheoLayerOL } = await import('./parisArcheoOL')
      return createParisArcheoLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Patrimoine Occitanie': {
    name: 'Sites Patrimoine Occitanie',
    factory: async () => {
      const { createOccitaniePatrimoineLayerOL } = await import('./occitaniePatrimoineOL')
      return createOccitaniePatrimoineLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Patrimoine PACA': {
    name: 'Sites Patrimoine PACA',
    factory: async () => {
      const { createPacaPatrimoineLayerOL } = await import('./pacaPatrimoineOL')
      return createPacaPatrimoineLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Patrimoine Normandie': {
    name: 'Sites Patrimoine Normandie',
    factory: async () => {
      const { createNormandiePatrimoineLayerOL } = await import('./normandiePatrimoineOL')
      return createNormandiePatrimoineLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Maginotlinie': {
    name: 'Maginotlinie',
    factory: async () => {
      const { createWikimaginotLayerOL } = await import('./wikimaginotOL')
      return createWikimaginotLayerOL()
    },
    immediateLoad: false,
    regions: ['fr', 'de']
  },
  // Sites Classés per regio
  'Sites Classés Bretagne': {
    name: 'Sites Classés Bretagne',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Bretagne')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Normandie': {
    name: 'Sites Classés Normandie',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Normandie')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Hauts-de-France': {
    name: 'Sites Classés Hauts-de-France',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Hauts-de-France')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Grand Est': {
    name: 'Sites Classés Grand Est',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Grand Est')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Île-de-France': {
    name: 'Sites Classés Île-de-France',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Île-de-France')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Centre-Val de Loire': {
    name: 'Sites Classés Centre-Val de Loire',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Centre-Val de Loire')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Bourgogne-FC': {
    name: 'Sites Classés Bourgogne-FC',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Bourgogne-FC')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Pays de la Loire': {
    name: 'Sites Classés Pays de la Loire',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Pays de la Loire')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Nouvelle-Aquitaine': {
    name: 'Sites Classés Nouvelle-Aquitaine',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Nouvelle-Aquitaine')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Auvergne-RA': {
    name: 'Sites Classés Auvergne-RA',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Auvergne-RA')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Occitanie': {
    name: 'Sites Classés Occitanie',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Occitanie')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés PACA': {
    name: 'Sites Classés PACA',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés PACA')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Sites Classés Corse': {
    name: 'Sites Classés Corse',
    factory: async () => {
      const { createSitesClassesRegionLayerOL } = await import('./sitesClassesRegionOL')
      return createSitesClassesRegionLayerOL('Sites Classés Corse')
    },
    immediateLoad: false,
    regions: ['fr']
  },
  'Monumenten IDF': {
    name: 'Monumenten IDF',
    factory: async () => {
      const { createMonumentsIdfLayerOL } = await import('./monumentsIdfOL')
      return createMonumentsIdfLayerOL()
    },
    immediateLoad: false,
    regions: ['fr']
  },

}
